import { Alert, Badge, Button, Checkbox, Collapse, Group, Loader, Paper, Radio, Select, Stack, Text, TextInput, Textarea } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { TicketForm } from "@msc/shared";

import { isJiraReauthRequired } from "../../hooks/useCustomerView.js";
import { useSubmitForm, useTicketActions, useTicketForms } from "../../hooks/useCustomerViewActions.js";
import { initialAnswers, isChoiceType, isEditableType, missingRequired, setText, toggleChoice, type Answers } from "../../lib/customerView/formAnswers.js";
import { writeErrorText } from "../../lib/customerView/jiraValidation.js";
import { ReconnectBanner } from "./ReconnectBanner.js";

function FormCard({ form, ticketKey, objectKey }: { form: TicketForm; ticketKey: string; objectKey: string | null }) {
  const { t } = useTranslation();
  const [answers, setAnswers] = useState<Answers>(() => initialAnswers(form));
  const submit = useSubmitForm(objectKey);
  const readOnly = form.submitted || form.locked;
  const missing = missingRequired(form, answers);

  // Jede Antwort-Änderung räumt ein altes Fehler-Alert weg — sonst hängt die
  // Meldung eines vorherigen, längst korrigierten Versuchs weiter im Bild.
  const change = (fn: (a: Answers) => Answers) => {
    // reset() nur nach einem Fehler: Während der Übertragung würde es den
    // laufenden Aufruf vom Observer trennen (Spinner weg, Fehler unsichtbar,
    // zweiter Submit möglich).
    if (submit.isError) submit.reset();
    setAnswers(fn);
  };
  const locked = readOnly || submit.isPending;

  return (
    <Paper withBorder p="sm" radius="sm">
      <Group justify="space-between" mb="xs">
        <Text fw={600} size="sm">{form.name}</Text>
        <Group gap={4}>
          {form.internal && <Badge size="xs" variant="light" color="gray">{t("pages.customerView.cockpit.internal")}</Badge>}
          {form.submitted && <Badge size="xs" color="green">{t("pages.customerView.cockpit.actions.formSubmitted")}</Badge>}
          {form.locked && <Badge size="xs" color="gray">{t("pages.customerView.cockpit.actions.formLocked")}</Badge>}
        </Group>
      </Group>
      <Stack gap="sm">
        {form.questions.map((q) => (
          <Stack key={q.id} gap={4}>
            {!isEditableType(q.type) && (
              <>
                <Text size="sm">{q.label}{q.required ? " *" : ""}</Text>
                {q.description && <Text size="xs" c="dimmed">{q.description}</Text>}
                <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.actions.formReadOnly")}</Text>
              </>
            )}
            {q.type === "cm" && (
              <Checkbox.Group label={q.label} required={q.required} description={q.description}
                value={answers[q.id]?.choices ?? []}
                onChange={(values) => change((a) => ({ ...a, [q.id]: { choices: values } }))}>
                <Stack gap={4} mt={4}>
                  {q.choices.map((c) => <Checkbox key={c.id} size="sm" value={c.id} label={c.label} disabled={locked} />)}
                </Stack>
              </Checkbox.Group>
            )}
            {q.type === "cs" && (
              <Radio.Group label={q.label} required={q.required} description={q.description}
                value={answers[q.id]?.choices?.[0] ?? null} onChange={(v) => change((a) => toggleChoice(a, q.id, v, false))}>
                <Stack gap={4}>{q.choices.map((c) => <Radio key={c.id} size="sm" value={c.id} label={c.label} disabled={locked} />)}</Stack>
              </Radio.Group>
            )}
            {q.type === "cd" && (
              <Select size="xs" label={q.label} required={q.required} description={q.description}
                data={q.choices.map((c) => ({ value: c.id, label: c.label }))} value={answers[q.id]?.choices?.[0] ?? null}
                clearable
                onChange={(v) => change((a) => (v ? toggleChoice(a, q.id, v, false) : { ...a, [q.id]: { choices: [] } }))} disabled={locked} />
            )}
            {q.type === "ts" && (
              <TextInput size="xs" label={q.label} required={q.required} description={q.description}
                value={answers[q.id]?.text ?? ""} onChange={(e) => change((a) => setText(a, q.id, e.currentTarget.value))} disabled={locked} />
            )}
            {q.type === "tl" && (
              <Textarea size="xs" autosize minRows={2} label={q.label} required={q.required} description={q.description}
                value={answers[q.id]?.text ?? ""} onChange={(e) => change((a) => setText(a, q.id, e.currentTarget.value))} disabled={locked} />
            )}
            {isChoiceType(q.type) && q.choices.length === 0 && <Text size="xs" c="dimmed">—</Text>}
          </Stack>
        ))}
        {!readOnly && (
          <Group justify="space-between" align="center">
            <Text size="xs" c={missing.length ? "orange" : "dimmed"}>
              {missing.length ? t("pages.customerView.cockpit.actions.formMissing", { count: missing.length }) : ""}
            </Text>
            <Button size="xs" loading={submit.isPending} disabled={missing.length > 0}
              onClick={() => submit.mutate({ key: ticketKey, formId: form.id, answers })}>
              {t("pages.customerView.cockpit.actions.formSubmit")}
            </Button>
          </Group>
        )}
        {submit.isError && (
          isJiraReauthRequired(submit.error) ? <ReconnectBanner /> : (
            <Alert color="red" variant="light">{writeErrorText(submit.error, t, "pages.customerView.cockpit.actions.saveFailed")}</Alert>
          )
        )}
      </Stack>
    </Paper>
  );
}

/**
 * Angehängte JSM-Formulare — im Prozess die Lösungscheckliste vor „Erledigt".
 * Standardmäßig eingeklappt: Im Termin steht das Gespräch im Vordergrund, die
 * Checkliste kommt erst beim Abschluss dran. Die Kopfzeile nennt, wie viele
 * Formulare noch offen sind, damit der Lead sie nicht übersieht.
 *
 * Die Kopfzeile zählt aus dem Formular-INDEX von `/actions` — denselben lädt
 * das Statusmenü ohnehin, der Cache ist geteilt. Die vollen Formulare
 * (`/forms`, je ein Jira-Aufruf pro Formular) kommen erst beim Aufklappen.
 * Sonst kostete im Cockpit jeder Ticketwechsel zusätzliche Jira-Aufrufe für
 * ein Panel, das fast immer zu bleibt (OFFENE-PUNKTE CV11).
 */
export function ChecklistPanel({ ticketKey, objectKey }: { ticketKey: string; objectKey: string | null }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const actions = useTicketActions(ticketKey);
  const forms = useTicketForms(open ? ticketKey : null);
  if (actions.isLoading) return <Loader size="xs" />;
  if (actions.isError) {
    return isJiraReauthRequired(actions.error) ? <ReconnectBanner /> : <Text size="sm" c="red">{t("pages.customerView.cockpit.actions.formsError")}</Text>;
  }
  const index = actions.data?.forms ?? [];
  const openCount = index.filter((f) => !f.submitted).length;
  const list = forms.data?.forms ?? [];
  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Button variant="subtle" size="compact-sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? t("pages.customerView.cockpit.actions.hideForms") : t("pages.customerView.cockpit.actions.showForms", { count: index.length })}
        </Button>
        {openCount > 0 && <Badge size="xs" color="orange" variant="light">{t("pages.customerView.cockpit.actions.formsOpen", { count: openCount })}</Badge>}
      </Group>
      <Collapse in={open}>
        <Stack gap="xs">
          {forms.isLoading && <Loader size="xs" />}
          {forms.isError && (
            isJiraReauthRequired(forms.error) ? <ReconnectBanner /> : <Text size="sm" c="red">{t("pages.customerView.cockpit.actions.formsError")}</Text>
          )}
          {forms.isSuccess && list.length === 0 && <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.actions.formNone")}</Text>}
          {list.map((f) => <FormCard key={`${f.id}:${f.submitted}:${f.locked}`} form={f} ticketKey={ticketKey} objectKey={objectKey} />)}
        </Stack>
      </Collapse>
    </Stack>
  );
}
